import { db } from '@/data/firebase/config';
import { ALLOWED_DOMAINS } from '@/domain/env/allowed-domains';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { User } from '@/domain/types/user';
import Button from '@/presentation/components/button';
import Drawer from '@/presentation/components/drawer';
import {
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
// import { writeFileSync } from 'fs';
import React, { useEffect, useState } from 'react';
import { FaUsers } from 'react-icons/fa';
import * as XLSX from 'xlsx';
// Exportar usuarios a Excel, una hoja por dominio
function exportUsersByDomainToExcel(users: UserWithCreatedAt[]) {
  // Agrupar usuarios por dominio
  const domainMap: Record<string, UserWithCreatedAt[]> = {};
  users.forEach((user) => {
    const domain =
      user.email && typeof user.email === 'string' && user.email.includes('@')
        ? user.email.split('@')[1]
        : 'Sin dominio';
    if (!domainMap[domain]) domainMap[domain] = [];
    domainMap[domain].push(user);
  });

  const wb = XLSX.utils.book_new();
  Object.entries(domainMap).forEach(([domain, users]) => {
    const data = users.map((u) => [
      u.document,
      `${u.name} ${u.lastname}`,
      u.email,
    ]);
    const ws = XLSX.utils.aoa_to_sheet([
      ['Cédula', 'Nombre', 'Correo'],
      ...data,
    ]);
    XLSX.utils.book_append_sheet(wb, ws, domain.substring(0, 31));
  });
  XLSX.writeFile(wb, 'usuarios_por_dominio.xlsx');
}
// Exportar dominios únicos a CSV
function exportDomainsToCSV(domains: string[]) {
  const csvContent = 'data:text/csv;charset=utf-8,' + domains.join('\n');
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', 'dominios_unicos.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

interface DomainCardProps {
  domain: string;
  count: number;
  activeCount: number;
  blockedCount: number;
  onShowUsers: () => void;
  isAllowed: boolean;
}

const DomainCard: React.FC<DomainCardProps> = ({
  domain,
  count,
  activeCount,
  blockedCount,
  onShowUsers,
  isAllowed,
}) => (
  <div
    className={`bg-white shadow rounded-lg p-4 flex flex-col items-center gap-2 border ${isAllowed ? 'border-green-400' : 'border-gray-200'}`}
  >
    <div className="flex items-center gap-2 w-full justify-between">
      <span className="text-lg font-bold break-all">{domain}</span>
      <span
        className={`px-2 py-0.5 rounded-full text-xs font-semibold ${isAllowed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
      >
        {isAllowed ? 'En lista blanca' : 'No permitido'}
      </span>
    </div>
    <span className="text-sm text-gray-500">{count} usuarios</span>
    <div className="flex gap-2 text-xs">
      <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
        {activeCount} activos
      </span>
      <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">
        {blockedCount} bloqueados
      </span>
    </div>
    <div className="flex items-center gap-3 mt-2 w-full justify-between">
      <Button
        label="Ver usuarios"
        variant="ghost"
        onClick={onShowUsers}
        icon={<FaUsers />}
        className="text-xs px-2 py-1 h-8"
      />
    </div>
  </div>
);

type UserWithCreatedAt = User & { createdAt?: string | Date | null };

const DomainsManager: React.FC = () => {
  const [users, setUsers] = useState<UserWithCreatedAt[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'active' | 'blocked'
  >('all');
  // Toast
  const showToast = (msg: string) => {
    if (typeof window !== 'undefined' && (window as any).toast) {
      (window as any).toast.success(msg, { theme: 'colored' });
    }
  };
  // Bloqueo masivo de usuarios por dominio
  // Borrado lógico en colecciones relacionadas
  const updateRelatedDocs = async (
    user: UserWithCreatedAt,
    isUserBlocked: boolean,
  ) => {
    // Comentarios
    const commentsRef = collection(db, 'comments');
    const qComments = query(
      commentsRef,
      where('document', '==', user.document),
    );
    const commentsSnap = await getDocs(qComments);
    const batchComments = writeBatch(db);
    commentsSnap.forEach((docu) => {
      batchComments.update(docu.ref, { isUserBlocked });
    });
    await batchComments.commit();

    // Histórico
    const historicalRef = collection(db, DOCUMENTS_DB.HISTORICAL);
    const qHist = query(historicalRef, where('document', '==', user.document));
    const histSnap = await getDocs(qHist);
    const batchHist = writeBatch(db);
    histSnap.forEach((docu) => {
      batchHist.update(docu.ref, { isUserBlocked });
    });
    await batchHist.commit();
  };

  // Solo usar la lista blanca importada del archivo
  const allowedDomains = ALLOWED_DOMAINS;

  useEffect(() => {
    const fetchUsers = async () => {
      const snapshot = await getDocs(collection(db, DOCUMENTS_DB.USERS));
      setUsers(
        snapshot.docs.map((doc) => ({
          ...(doc.data() as User),
          id: doc.id,
          createdAt:
            (doc.data() as any).createdAt ||
            (doc as any).createTime?.toDate?.() ||
            null,
        })),
      );
    };
    fetchUsers();
  }, []);

  // Calcular totales, activos y bloqueados por dominio
  const domainStats: Record<
    string,
    { total: number; active: number; blocked: number }
  > = {};
  users.forEach((user) => {
    const domain =
      user.email && typeof user.email === 'string' && user.email.includes('@')
        ? user.email.split('@')[1]
        : '';
    if (!domainStats[domain]) {
      domainStats[domain] = { total: 0, active: 0, blocked: 0 };
    }
    domainStats[domain].total++;
    if (user.isEnabled) {
      domainStats[domain].active++;
    } else {
      domainStats[domain].blocked++;
    }
  });

  const handleShowUsers = (domain: string) => {
    setSelectedDomain(domain);
    setDrawerOpen(true);
  };

  const [usersPage, setUsersPage] = useState(1);
  const USERS_PER_PAGE = 50;
  const filteredUsersInDomain = users.filter((user) => {
    if (
      !user.email ||
      typeof user.email !== 'string' ||
      !user.email.includes('@')
    )
      return false;
    if (user.email.split('@')[1] !== selectedDomain) return false;
    const searchLower = search.trim().toLowerCase();
    if (
      searchLower &&
      !(
        (user.name && user.name.toLowerCase().includes(searchLower)) ||
        (user.lastname && user.lastname.toLowerCase().includes(searchLower)) ||
        (user.email && user.email.toLowerCase().includes(searchLower))
      )
    ) {
      return false;
    }
    if (statusFilter === 'active' && !user.isEnabled) return false;
    if (statusFilter === 'blocked' && user.isEnabled) return false;
    return true;
  });
  const usersInDomain = filteredUsersInDomain.slice(
    0,
    usersPage * USERS_PER_PAGE,
  );

  // Reset paginación al cambiar filtro o búsqueda o dominio
  React.useEffect(() => {
    setUsersPage(1);
  }, [search, statusFilter, selectedDomain]);

  // Dominios únicos
  const uniqueDomains = Object.keys(domainStats).filter((d) => d);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
        <div>
          <h2 className="text-xl font-bold">Lista blanca de dominios</h2>
          <div className="flex gap-2 mt-1 text-xs">
            <span className="text-gray-600">
              Total de usuarios: {users.length}
            </span>
            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
              {users.filter((u) => u.isEnabled).length} activos
            </span>
            <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">
              {users.filter((u) => !u.isEnabled).length} bloqueados
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            label="Exportar dominios únicos"
            variant="primary"
            onClick={() => exportDomainsToCSV(uniqueDomains)}
            className="text-xs h-8"
          />
          <Button
            label="Exportar usuarios por dominio"
            variant="primary"
            onClick={() => exportUsersByDomainToExcel(users)}
            className="text-xs h-8"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Object.entries(domainStats).map(([domain, stats]) => (
          <DomainCard
            key={domain}
            domain={domain}
            count={stats.total}
            activeCount={stats.active}
            blockedCount={stats.blocked}
            onShowUsers={() => handleShowUsers(domain)}
            isAllowed={allowedDomains.includes(domain)}
          />
        ))}
      </div>
      <Drawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={`Usuarios del dominio ${selectedDomain}`}
        widthClass="w-full md:w-2/3 lg:w-2/3"
      >
        <div className="p-4">
          {/* Solo un listado, tabla y controles abajo */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
            <div className="flex gap-2 flex-1">
              <input
                type="text"
                placeholder="Buscar por nombre o correo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border px-2 py-1 rounded w-full text-xs"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="border px-2 py-1 rounded text-xs"
              >
                <option value="all">Todos</option>
                <option value="active">Activos</option>
                <option value="blocked">Bloqueados</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="flex gap-2 mb-2">
              <Button
                label="Bloquear todos filtrados"
                variant="danger"
                isLoading={isBlocking}
                onClick={async () => {
                  setIsBlocking(true);
                  const toBlock = usersInDomain.filter((u) => u.isEnabled);
                  const batchSize = 200;
                  for (let i = 0; i < toBlock.length; i += batchSize) {
                    const batch = writeBatch(db);
                    const chunk = toBlock.slice(i, i + batchSize);
                    for (const user of chunk) {
                      const ref = doc(db, DOCUMENTS_DB.USERS, user.id);
                      batch.update(ref, { isEnabled: false });
                      await updateRelatedDocs(user, true);
                    }
                    await batch.commit();
                  }
                  setUsers((prev) =>
                    prev.map((u) =>
                      toBlock.some((bu) => bu.id === u.id)
                        ? { ...u, isEnabled: false }
                        : u,
                    ),
                  );
                  setIsBlocking(false);
                  showToast('Usuarios bloqueados correctamente');
                }}
                disabled={isBlocking || !usersInDomain.some((u) => u.isEnabled)}
              />
              <Button
                label="Desbloquear todos filtrados"
                variant="primary"
                isLoading={isBlocking}
                onClick={async () => {
                  setIsBlocking(true);
                  const toUnblock = usersInDomain.filter((u) => !u.isEnabled);
                  const batchSize = 200;
                  for (let i = 0; i < toUnblock.length; i += batchSize) {
                    const batch = writeBatch(db);
                    const chunk = toUnblock.slice(i, i + batchSize);
                    for (const user of chunk) {
                      const ref = doc(db, DOCUMENTS_DB.USERS, user.id);
                      batch.update(ref, { isEnabled: true });
                      await updateRelatedDocs(user, false);
                    }
                    await batch.commit();
                  }
                  setUsers((prev) =>
                    prev.map((u) =>
                      toUnblock.some((bu) => bu.id === u.id)
                        ? { ...u, isEnabled: true }
                        : u,
                    ),
                  );
                  setIsBlocking(false);
                  showToast('Usuarios desbloqueados correctamente');
                }}
                disabled={
                  isBlocking || !usersInDomain.some((u) => !u.isEnabled)
                }
              />
            </div>
            <table className="min-w-full border border-gray-200 text-xs">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-2 py-1 border">Nombre</th>
                  <th className="px-2 py-1 border">Correo</th>
                  <th className="px-2 py-1 border">Bloqueado</th>
                </tr>
              </thead>
              <tbody>
                {usersInDomain.map((user) => (
                  <tr key={user.id} className="border-b">
                    <td className="px-2 py-1">
                      {user.name} {user.lastname}
                    </td>
                    <td className="px-2 py-1">{user.email}</td>
                    <td className="px-2 py-1">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!user.isEnabled}
                          onChange={async (e) => {
                            const checked = e.target.checked;
                            const ref = doc(db, DOCUMENTS_DB.USERS, user.id);
                            await updateDoc(ref, { isEnabled: !checked });
                            setUsers((prev) =>
                              prev.map((u) =>
                                u.id === user.id
                                  ? { ...u, isEnabled: !checked }
                                  : u,
                              ),
                            );
                            showToast(
                              !checked
                                ? 'Usuario habilitado correctamente'
                                : 'Usuario bloqueado correctamente',
                            );
                          }}
                        />
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${user.isEnabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                        >
                          {user.isEnabled ? 'Activo' : 'Bloqueado'}
                        </span>
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {usersInDomain.length < filteredUsersInDomain.length && (
              <div className="flex justify-center my-2">
                <button
                  className="px-4 py-2 text-xs bg-gray-200 rounded hover:bg-gray-300"
                  onClick={() => setUsersPage((p) => p + 1)}
                >
                  Cargar más usuarios
                </button>
              </div>
            )}
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default DomainsManager;
